import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  ComposedChart, Area, Line, Legend } from 'recharts';

const COLORS = ['#236844', '#2f7d52', '#3c9263', '#f59e0b', '#8b5cf6'];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatform = user?.role === 'platform_admin';
  const [summary, setSummary] = useState<any>(null);
  const [collections, setCollections] = useState<any[]>([]);
  const [methods, setMethods] = useState<any[]>([]);
  const [activity, setActivity] = useState<any>(null);
  const [platform, setPlatform] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isPlatform) {
      api.get('/admin/analytics/platform-overview?months=12')
        .then((p) => setPlatform(p))
        .catch(() => setPlatform(null))
        .finally(() => setLoading(false));
      return;
    }
    Promise.all([
      api.get('/admin/analytics/dashboard').catch(() => null),
      api.get('/admin/analytics/collection-by-period?months=6').catch(() => []),
      api.get('/admin/analytics/payment-methods').catch(() => []),
      api.get('/admin/analytics/recent-activity').catch(() => null),
    ]).then(([s, c, m, a]) => {
      setSummary(s);
      setCollections(c || []);
      setMethods(m || []);
      setActivity(a);
    }).finally(() => setLoading(false));
  }, [isPlatform]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#5f6f66' }}>Уншиж байна...</div>;

  if (isPlatform) return <PlatformDashboard platform={platform} navigate={navigate} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontFamily: "'Inter'", fontWeight: 700, fontSize: 24, color: '#1c2b22', margin: 0 }}>Хянах самбар</h2>
      </div>

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
          <StatCard label="Нийт орон сууц" value={summary.units ?? 0} color="#236844" />
          <StatCard label="Нийт оршин суугч" value={summary.residents ?? 0} color="#2f7d52" />
          <StatCard label="Нийт нэхэмжлэх" value={`₮${(summary.total_billed ?? 0).toLocaleString()}`} color="#3c9263" />
          <StatCard label="Хугацаа хэтэрсэн" value={summary.overdue_invoices ?? 0} color="#ef4444" />
          <StatCard label="Цуглуулсан" value={`₮${(summary.total_collected ?? 0).toLocaleString()}`} color="#8b5cf6" />
          <StatCard label="Цуглуулалт" value={`${summary.collection_rate ?? 0}%`} color="#06b6d4" />
          <StatCard label="Нээлттэй өргөдөл" value={summary.open_requests ?? 0} color="#f59e0b" />
          <StatCard label="Шийдвэрлэж буй" value={summary.in_progress_requests ?? 0} color="#3b82f6" />
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 28 }}>
        {[
          { label: 'Нэхэмжлэх үүсгэх', path: '/invoices', icon: '📄', color: '#236844' },
          { label: 'Зарлал нийтлэх', path: '/announcements', icon: '📢', color: '#f59e0b' },
          { label: 'Өргөдөл шалгах', path: '/maintenance', icon: '🔧', color: '#3b82f6', badge: activity?.pending_maintenance_count },
          { label: 'Тайлан татах', path: '/reports', icon: '📊', color: '#8b5cf6' },
        ].map(a => (
          <button key={a.path} onClick={() => navigate(a.path)} style={{
            background: '#fff', border: 'none', borderRadius: 12, padding: '16px 14px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'transform .1s',
          }}>
            <span style={{ fontSize: 24 }}>{a.icon}</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#1c2b22', textAlign: 'left' }}>{a.label}</span>
            {a.badge ? <span style={{
              background: '#ef4444', color: '#fff', borderRadius: 10, padding: '2px 7px', fontSize: 11, fontWeight: 700, marginLeft: 'auto',
            }}>{a.badge}</span> : null}
          </button>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 28 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 16, color: '#1c2b22', marginBottom: 16, marginTop: 0 }}>Цуглуулалт (сараар)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={collections}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e5dc" />
              <XAxis dataKey="period" tick={{ fill: '#5f6f66', fontSize: 12 }} />
              <YAxis tick={{ fill: '#5f6f66', fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="collected" fill="#2f7d52" name="Цуглуулсан" radius={[4, 4, 0, 0]} />
              <Bar dataKey="billed" fill="#e8e5dc" name="Нийт" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 16, color: '#1c2b22', marginBottom: 16, marginTop: 0 }}>Төлбөрийн арга</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={methods} dataKey="count" nameKey="method" cx="50%" cy="50%" outerRadius={90} label>
                {methods.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      {activity && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
          <ActivityCard title="Сүүлийн төлбөр" items={activity.recent_payments?.map((p: any) => ({
            label: `₮${p.amount?.toLocaleString()}`,
            sub: p.method === 'qpay' ? 'QPay' : p.method === 'bank_transfer' ? 'Банк' : p.method === 'cash' ? 'Бэлэн' : p.method,
            date: p.date,
          })) || []} emptyText="Төлбөр олдсонгүй" />

          <ActivityCard title="Сүүлийн өргөдөл" items={activity.recent_maintenance?.map((m: any) => ({
            label: m.title,
            sub: m.status === 'pending' ? 'Хүлээгдэж буй' : m.status === 'in_progress' ? 'Шийдвэрлэж буй' : m.status === 'resolved' ? 'Шийдсэн' : m.status,
            date: m.date,
            statusColor: m.status === 'pending' ? '#f59e0b' : m.status === 'resolved' ? '#236844' : '#3b82f6',
          })) || []} emptyText="Өргөдөл олдсонгүй" />

          <ActivityCard title="Сүүлийн зарлал" items={activity.recent_announcements?.map((a: any) => ({
            label: a.title,
            sub: `${a.view_count} уншсан`,
            date: a.date,
          })) || []} emptyText="Зарлал олдсонгүй" />
        </div>
      )}
    </div>
  );
}

function PlatformDashboard({ platform, navigate }: { platform: any; navigate: (p: string) => void }) {
  if (!platform) {
    return <div style={{ textAlign: 'center', padding: 60, color: '#5f6f66' }}>Мэдээлэл ачаалж чадсангүй.</div>;
  }
  const growth = platform.growth || [];
  const byStatus = platform.by_status || [];
  const byDistrict = (platform.by_district || []).slice(0, 8);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontFamily: "'Inter'", fontWeight: 700, fontSize: 24, color: '#1c2b22', margin: 0 }}>Платформ хянах самбар</h2>
        <button onClick={() => navigate('/district')} style={{
          background: '#fff', border: '1px solid #dcdacf', borderRadius: 10, padding: '8px 16px',
          cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#236844',
        }}>Бүх СӨХ харах →</button>
      </div>

      {/* Тойм картууд */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard label="Нийт бүртгэлтэй СӨХ" value={platform.total ?? 0} color="#236844" />
        <StatCard label="Идэвхтэй" value={platform.active ?? 0} color="#2f7d52" />
        <StatCard label="Туршилтын" value={platform.trial ?? 0} color="#f59e0b" />
        <StatCard label="Түдгэлзсэн" value={platform.suspended ?? 0} color="#ef4444" />
      </div>

      {/* Өсөлтийн график */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 28 }}>
        <h3 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 16, color: '#1c2b22', marginBottom: 16, marginTop: 0 }}>
          Бүртгэлтэй СӨХ-ийн өсөлт (сүүлийн 12 сар)
        </h3>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={growth}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e5dc" />
            <XAxis dataKey="period" tick={{ fill: '#5f6f66', fontSize: 12 }} />
            <YAxis tick={{ fill: '#5f6f66', fontSize: 12 }} allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="cumulative" name="Нийт (хуримтлагдсан)" fill="#eaf3ec" stroke="#236844" strokeWidth={2} />
            <Bar dataKey="new" name="Шинэ бүртгэл" fill="#2f7d52" radius={[4, 4, 0, 0]} barSize={22} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Төлөв + дүүрэг */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 16, color: '#1c2b22', marginBottom: 16, marginTop: 0 }}>Төлвөөр</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={byStatus} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label>
                {byStatus.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 16, color: '#1c2b22', marginBottom: 16, marginTop: 0 }}>Дүүргээр (топ 8)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byDistrict} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e5dc" />
              <XAxis type="number" tick={{ fill: '#5f6f66', fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="district" tick={{ fill: '#5f6f66', fontSize: 12 }} width={90} />
              <Tooltip />
              <Bar dataKey="count" name="СӨХ тоо" fill="#3c9263" radius={[0, 4, 4, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 20,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ fontSize: 13, color: '#5f6f66', marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "'Inter'", fontSize: 24, fontWeight: 700, color: '#1c2b22' }}>{value}</div>
    </div>
  );
}

function ActivityCard({ title, items, emptyText }: { title: string; items: any[]; emptyText: string }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <h4 style={{ fontFamily: "'Inter'", fontWeight: 600, fontSize: 14, color: '#1c2b22', marginTop: 0, marginBottom: 12 }}>{title}</h4>
      {items.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>{emptyText}</div>
      ) : items.map((item: any, i: number) => (
        <div key={i} style={{ padding: '8px 0', borderBottom: i < items.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#1c2b22', marginBottom: 2 }}>{item.label}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: item.statusColor || '#94a3b8' }}>{item.sub}</span>
            {item.date && <span style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(item.date).toLocaleDateString('mn-MN')}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
